import { useState } from 'react'

import moment from 'moment'
import {
  ChartLine,
  ChartPie,
  Clock,
  EyeInvisible,
  EyeVisible,
  Target,
} from '@factorialco/f0-react/icons/app'
import { F0Box, F0Card, F0Icon } from '@factorialco/f0-react'
import { NumberInput, Switch } from '@factorialco/f0-react/dist/experimental'
import {
  GoalsMetricValueStatusEnum,
  PerformanceGoalsMetricRowFragment,
  PerformanceGoalsUpdateQualitativeMetricDocument,
  PerformanceGoalsUpdateQuantitativeMetricDocument,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'

import { useDebounceCallback } from 'usehooks-ts'

import { useMutation } from 'modules/core/hooks/useMutation'
import { getUIDuration } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { UpdateMetricModal } from 'modules/goals/components/UpdateMetricModal'
import { normalizeMetric } from 'modules/goals/lib/normalizeMetric'

type SingleProps = {
  type: 'single'
  metric: PerformanceGoalsMetricRowFragment
  isGoalPublic: boolean
  refetchQueries?: DocumentNode[]
  onViewHistory?: () => void
}

type MultiProps = {
  type: 'multi'
  progress: number
  needsAttention: boolean
}

export type ProgressCardProps = SingleProps | MultiProps

function buildProgressMetadata(progressPercentage: number, needsAttention: boolean) {
  const color: 'feedback-neutral' | 'feedback-positive' = needsAttention
    ? 'feedback-neutral'
    : 'feedback-positive'

  return [
    {
      icon: ChartPie,
      property: {
        type: 'progressBar' as const,
        value: {
          color,
          label: `${i18n.formatNumber(progressPercentage)}%`,
          max: 100,
          value: progressPercentage,
        },
      },
      tooltip: i18n.t('goals_revamp.detail.progress_title'),
    },
  ]
}

export const ProgressCard: React.FC<ProgressCardProps> = (props) => {
  if (props.type === 'multi') {
    const progressPercentage = Math.min(props.progress, 100)
    const metadata = buildProgressMetadata(progressPercentage, props.needsAttention)

    return <F0Card title={i18n.t('goals_revamp.detail.progress_title')} metadata={metadata} />
  }

  return (
    <SingleKRProgressCard
      metric={props.metric}
      isGoalPublic={props.isGoalPublic}
      refetchQueries={props.refetchQueries}
      onViewHistory={props.onViewHistory}
    />
  )
}

const SingleKRProgressCard: React.FC<{
  metric: PerformanceGoalsMetricRowFragment
  isGoalPublic: boolean
  refetchQueries?: DocumentNode[]
  onViewHistory?: () => void
}> = ({ metric, isGoalPublic, refetchQueries, onViewHistory }) => {
  const normalizedMetric = normalizeMetric(metric)
  const canEdit = metric._policies?.update?.can
  const needsAttention =
    normalizedMetric.progressAssessmentStatus === 'at_risk' ||
    normalizedMetric.progressAssessmentStatus === 'highly_at_risk'
  const quantitativeValue =
    normalizedMetric.type === 'quantitative' ? normalizedMetric.quantitativeValue : null
  const progressPercentage = Math.min(metric.progressPercentageValue, 100)

  const [editing, setEditing] = useState(false)
  const [displayValue, setDisplayValue] = useState(quantitativeValue?.currentValue ?? 0)

  const [updateQuantitativeMetric] = useMutation(PerformanceGoalsUpdateQuantitativeMetricDocument, {
    extractorFn: (d) => d?.goalsMutations?.updateQuantitativeCurrentValueGoalsMetric,
    refetchQueries: refetchQueries ?? [],
    awaitRefetchQueries: true,
  })

  const [updateQualitativeMetric] = useMutation(PerformanceGoalsUpdateQualitativeMetricDocument, {
    extractorFn: (d) => d?.goalsMutations?.updateQualitativeCurrentStatusGoalsMetric,
    refetchQueries: refetchQueries ?? [],
    awaitRefetchQueries: true,
  })

  const debouncedUpdate = useDebounceCallback((newValue: number) => {
    updateQuantitativeMetric({ variables: { id: metric.id, value: newValue } })
  }, 1500)

  const handleQuantitativeChange = (value: number | null) => {
    const newValue = value ?? 0
    setDisplayValue(newValue)
    debouncedUpdate(newValue)
  }

  const handleQualitativeChange = (checked: boolean) => {
    const status: GoalsMetricValueStatusEnum = checked ? 'achieved' : 'not_achieved'
    updateQualitativeMetric({ variables: { id: metric.id, status } })
  }

  const metadata = buildProgressMetadata(progressPercentage, needsAttention)
  const deadlineLabel = getUIDuration({ date: moment(metric.endOn), withPrefixOrSuffix: true })

  const primaryAction = onViewHistory
    ? {
        icon: ChartLine,
        label: i18n.t('goals_revamp.detail.title'),
        onClick: onViewHistory,
      }
    : undefined

  const secondaryActions = canEdit
    ? [
        {
          label: i18n.t('common.edit'),
          onClick: () => setEditing(true),
        },
      ]
    : undefined

  return (
    <>
      <F0Card
        title={i18n.t('goals_revamp.detail.progress_title')}
        fullHeight
        metadata={metadata}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
      >
        <F0Box display="flex" flexDirection="column" gap="xl" position="relative" marginTop="md">
          <F0Box
            display="flex"
            flexDirection="row"
            gap="sm"
            alignItems="center"
            flexWrap="wrap"
            justifyContent="start"
          >
            {normalizedMetric.type === 'quantitative' && quantitativeValue && (
              <F0Box display="flex" flexDirection="row" gap="sm" alignItems="center">
                <F0Icon icon={Target} color="default" />
                <NumberInput
                  label={i18n.t('goals_revamp.detail.current_value')}
                  hideLabel
                  hint=""
                  locale={i18n.locale}
                  disabled={!metric._policies?.updateQuantitativeCurrentValue?.can}
                  min={
                    quantitativeValue.startValue < quantitativeValue.targetValue
                      ? quantitativeValue.startValue
                      : undefined
                  }
                  value={displayValue}
                  step={1}
                  maxDecimals={2}
                  onChange={handleQuantitativeChange}
                  size="sm"
                />
                / {i18n.formatNumber(quantitativeValue.targetValue)}
              </F0Box>
            )}

            {normalizedMetric.type === 'qualitative' && (
              <Switch
                title={i18n.t('performance_goals.metric_row.completed')}
                disabled={!metric._policies?.updateQualitativeCurrentStatus?.can}
                checked={normalizedMetric.qualitativeValue === 'achieved'}
                onCheckedChange={handleQualitativeChange}
              />
            )}
          </F0Box>

          <F0Box display="flex" flexDirection="row" gap="sm" alignItems="center">
            <F0Icon icon={isGoalPublic ? EyeVisible : EyeInvisible} color="default" />
            {isGoalPublic
              ? i18n.t('goals_revamp.detail.visibility_public')
              : i18n.t('goals_revamp.detail.visibility_restricted')}
          </F0Box>

          <F0Box display="flex" flexDirection="row" gap="sm" alignItems="center">
            <F0Icon icon={Clock} color="default" />
            {deadlineLabel}
          </F0Box>
        </F0Box>
      </F0Card>

      {editing && (
        <UpdateMetricModal
          metric={normalizedMetric}
          onClose={() => setEditing(false)}
          refetchQueries={refetchQueries}
        />
      )}
    </>
  )
}
